import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { ManagerCalendarEvent } from "../../../api/schemas.js";
import { amoLeadUrl } from "../../../lib/amo-url.js";
import { lessonStatusBadgeClass, lessonStatusLabel } from "../../../lib/lesson-status.js";
import { formatTimeInZone, getDeviceTimeZone, shortZoneName } from "../../../lib/timezone.js";
import { CapacityBadge } from "./CapacityBadge.js";
import { TimezoneLabels } from "./TimezoneLabels.js";

function Row(props: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-muted-foreground text-sm">{props.label}</dt>
      <dd className="min-w-0 break-words text-right font-medium text-sm">{props.children}</dd>
    </div>
  );
}

function formatCoursePrice(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined) return "—";
  return new Intl.NumberFormat("ru-RU").format(amount) + (currency ? ` ${currency}` : "");
}

export interface LessonDetailSheetProps {
  /** Адрес аккаунта amo; без него ссылка на сделку не рисуется. */
  readonly amoBaseUrl?: string | null;
  readonly open: boolean;
  readonly event: ManagerCalendarEvent | null;
  readonly occupied: number;
  readonly capacity: number;
  readonly onClose: () => void;
  readonly onReschedule: () => void;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
  readonly onNoShow: () => void;
}

/**
 * Деталь урока — боковая панель, а не модалка: неделя остаётся на экране,
 * менеджер видит соседние слоты, пока решает, что делать с этим уроком.
 */
export function LessonDetailSheet(props: LessonDetailSheetProps) {
  const event = props.event;
  const deviceTz = getDeviceTimeZone();
  const venueTz = event?.venueTimezone ?? deviceTz;
  const start = event ? new Date(event.startTime) : null;
  const end =
    event && start
      ? event.endTime
        ? new Date(event.endTime)
        : new Date(start.getTime() + event.durationMinutes * 60_000)
      : null;
  const span =
    start && end ? `${formatTimeInZone(start, deviceTz)}–${formatTimeInZone(end, deviceTz)}` : "—";
  const venueSpan =
    start && venueTz !== deviceTz
      ? `${formatTimeInZone(start, venueTz)} ${shortZoneName(venueTz)}`
      : null;

  return (
    <Sheet
      open={props.open && event !== null}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full gap-0 rounded-l-3xl sm:max-w-md"
        data-testid="lesson-detail-dialog"
      >
        {event ? (
          <>
            <SheetHeader className="gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 font-semibold text-xs ${lessonStatusBadgeClass(event.status)}`}
                >
                  {lessonStatusLabel(event.status)}
                </span>
                <CapacityBadge occupied={props.occupied} capacity={props.capacity} />
              </div>
              <SheetTitle className="text-xl">{event.clientName ?? "Урок"}</SheetTitle>
              <SheetDescription>
                {event.clientPhone ?? "телефон не указан"}
                {" · "}
                урок {event.lessonOrder}
                {event.courseTotalLessons ? ` из ${event.courseTotalLessons}` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-4">
              <div className="rounded-2xl bg-muted px-3.5 py-3">
                <TimezoneLabels iso={event.startTime} venueTimeZone={event.venueTimezone} compact />
              </div>
              <dl className="divide-y divide-border">
                <Row label="Курс">{event.courseTitle ?? event.courseCode ?? "—"}</Row>
                <Row label="Время">
                  <span className="pwz-readout">{span}</span>
                  {venueSpan ? (
                    <span className="block text-muted-foreground text-xs">
                      на площадке <span className="pwz-readout">{venueSpan}</span>
                    </span>
                  ) : null}
                </Row>
                <Row label="Длительность">
                  <span className="pwz-readout">{event.durationMinutes}</span> мин
                </Row>
                <Row label="Площадка">{event.venueName ?? "—"}</Row>
                <Row label="Инструктор">{event.instructorName ?? "—"}</Row>
                <Row label="Стоимость курса">
                  <span className="pwz-readout">
                    {formatCoursePrice(event.coursePriceAmount, event.coursePriceCurrency)}
                  </span>
                </Row>
                <Row label="Зачисление">
                  <span className="pwz-readout break-all text-xs">{event.enrollmentId}</span>
                </Row>
                <Row label="Версия">
                  <span className="pwz-readout text-xs">v{event.version}</span>
                </Row>
              </dl>
            </div>

            <div className="mt-auto flex flex-col gap-2 border-t p-4">
              {event.status === "scheduled" ? (
                <>
                  <Button type="button" onClick={props.onEdit} data-testid="action-edit">
                    Изменить урок
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={props.onReschedule}
                      data-testid="action-reschedule"
                    >
                      Перенести
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={props.onNoShow}
                      data-testid="action-no-show"
                    >
                      Неявка
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={props.onCancel}
                    data-testid="action-cancel"
                  >
                    Отменить урок
                  </Button>
                </>
              ) : (
                <Button type="button" variant="outline" onClick={props.onClose}>
                  Закрыть
                </Button>
              )}
              {event.amoLeadId && props.amoBaseUrl ? (
                <>
                  <Separator />
                  <a
                    className="inline-flex items-center justify-center gap-1.5 py-1 font-semibold text-primary text-sm hover:underline"
                    href={amoLeadUrl(props.amoBaseUrl, event.amoLeadId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lesson-amo-link"
                  >
                    Открыть сделку в amoCRM
                    <ExternalLink className="size-3.5" />
                  </a>
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
